/* eslint-disable @typescript-eslint/no-explicit-any */
import BackendApiInterface, { CombinedVersionInfoJson, LabelJsonWrapper, QUERY_PARAM_SESSION_ID } from "../backend-api-interface";
import { get, has } from "lodash-es";
import { sleep } from "../../util/sleep";
import { retryWithBackoff, RetryOptions } from "../../util/retry";
import NoAuthPage from "../../pages/auth/NoAuthPage";
import { Dispatch } from "@reduxjs/toolkit";
import { MVisionAppClient } from "../../store/configurationTarget/mvision-client-list";
import loadExternalScript from "../../util/load-external-script";
import { getSessionId, getAppVersionHeader } from "../../environments";
import { convertResponseToError, getErrorMessageFromBackendValidationErrorJson, parseConcurrentSaveError, ConcurrentSaveErrorWrapper } from "../../util/errors";
import { parseSaveResponse, SaveResult, SAVE_RESPONSE_OK_MESSAGE } from "../save-response";


// IMPORTANT: cockpit already appends http://localhost prefix to all URLs
import apiUrls, { DEFAULT_USER_ID, getLabelingUrl } from "./backend-urls";
import { configurationTargetListFetched } from "../../store/configurationTarget/configurationTargetSlice";
import { convertModelTypeToApiString, convertModelTypeToExportApiString, ModelType } from "../../store/global-types/customization-types";
import { DeploymentConfigInfo } from "../../store/appConfig/appDeploymentInfoTypes";
import { WorkspaceLabeling } from "../../store/appVersion/labeling";
import { AppVersionInfo } from "../../store/appVersion/appVersionTypes";
import { BackupInfo } from "../../store/backup/backup-types";

const SCRIPT_LOAD_MAX_ATTEMPTS = 64;
const SCRIPT_LOAD_POLL_INTERVAL_MS = 250;

const COCKPIT_RETRY_OPTIONS: RetryOptions = {
    maxRetries: 5,
    initialDelayMs: 500,
    backoffMultiplier: 2,
    maxDelayMs: 8000,
};

let backendPort: string = '';
let environmentTag: string = '';
let cockpitDebugMode: boolean = false;

export const BACKEND_RESPONSE_OK_QUOTED = `"${SAVE_RESPONSE_OK_MESSAGE}"`;

/**
 * Parses raw cockpit HTTP response string into a SaveResult.
 * Exported for testability — used internally by sendJson.
 *
 * @returns SaveResult on success.
 * @throws on null/undefined response, unparseable JSON, concurrent save errors,
 *         or unrecognized response format (with the validation error message).
 */
export function parseCockpitSendJsonResponse(result: string | null | undefined): SaveResult {
    if (result === BACKEND_RESPONSE_OK_QUOTED) {
        return { success: true };
    }

    if (result == null) {
        throw new Error('Received no valid response from backend.');
    }

    let resultData: unknown;
    try {
        resultData = JSON.parse(result);
    } catch {
        throw new Error(`Received unexpected response from backend: ${String(result).substring(0, 200)}`);
    }

    const parsed = parseSaveResponse(resultData);
    if (parsed.success) {
        return parsed;
    }

    const concurrentError = parseConcurrentSaveError(resultData);
    if (concurrentError) {
        throw new ConcurrentSaveErrorWrapper(concurrentError);
    }

    // Throw with validation error message; caller may re-wrap as BackendValidationError.
    throw new Error(getErrorMessageFromBackendValidationErrorJson(resultData));
}

/** Current app version. This is being sent to backend along API calls so must be set explicitly. */
let _appVersion: AppVersionInfo | undefined = undefined;

/**
 * Wrapper for accessing MVision Configuration functions through Cockpit API.
 * IMPORTANT: Accessing this class will crash if cockpit is not loaded into environment!
 */
export default class CockpitApi implements BackendApiInterface {

    public init(dispatch: Dispatch, deploymentInfo: DeploymentConfigInfo, appVersion: AppVersionInfo | null) {
        // set local values
        if (!deploymentInfo.cockpitBackendPort || !deploymentInfo.cockpitEnvironmentTag) {
            throw new Error('Invalid configuration -- cannot initialize local cockpit api');
        }

        backendPort = deploymentInfo.cockpitBackendPort;
        environmentTag = deploymentInfo.cockpitEnvironmentTag;
        cockpitDebugMode = deploymentInfo.cockpitDebugMode || false;
        _appVersion = appVersion || undefined;

        // load cockpit API scripts if we're in a cockpit environment
        loadExternalScript('cockpit', '../base1/cockpit.js');

        // we don't need to do login here, but we do need to fetch the client list
        dispatch(configurationTargetListFetched());
    }

    private redactHeaders(headers: any): any {
        if (!headers) {
            return headers;
        }
        const redacted = { ...headers };
        for (const key of Object.keys(redacted)) {
            if (key.toLowerCase() === 'authorization') {
                redacted[key] = '***';
            }
        }
        return redacted;
    }

    private redactBody(body: string | undefined): string | undefined {
        if (body === undefined) {
            return undefined;
        }
        const SENSITIVE_BODY_KEYS = ['license'];
        try {
            const parsed = JSON.parse(body);
            if (typeof parsed === 'object' && parsed !== null) {
                for (const key of SENSITIVE_BODY_KEYS) {
                    if (key in parsed) {
                        parsed[key] = '***';
                    }
                }
                return JSON.stringify(parsed);
            }
        } catch {
            // Not valid JSON, return as-is
        }
        return body;
    }

    private debugLog(method: string, url: string, details: { headers?: any; queryParams?: any; body?: string; response?: any; error?: any }) {
        if (!cockpitDebugMode) {
            return;
        }
        const prefix = '[CockpitDebug]';
        if (details.response !== undefined || details.error !== undefined) {
            if (details.error !== undefined) {
                console.log(`${prefix} RESPONSE ${method} ${url} ERROR:`, details.error);
            } else {
                console.log(`${prefix} RESPONSE ${method} ${url}:`, details.response);
            }
        } else {
            console.log(`${prefix} REQUEST ${method} ${url}`, { headers: this.redactHeaders(details.headers), queryParams: details.queryParams, body: this.redactBody(details.body) });
        }
    }

    private getErrorMessageFromError(obj: any): string {
        if (has(obj, 'detail')) {
            return `An error was received from backend: ${obj.detail}`;
        }
        else if (has(obj, 'message')) {
            // not sure why typescript wants to cast obj as never here, so we're forcibly casting it to any
            return `An error was received from backend: ${(obj as any).message}`;
        }
        else {
            return 'Received no valid response from backend.';;
        }
    }

    private async getHttpClient(): Promise<any> {
        // see https://cockpit-project.org/guide/latest/cockpit-http.html

        let client = null;

        let attempt = 0;
        while (client === null && attempt < SCRIPT_LOAD_MAX_ATTEMPTS) {
            attempt++;
            try {
                client = (window as any).cockpit.http(backendPort);
            }
            catch (err) {
                if (err instanceof TypeError && (err as TypeError).message === 'window.cockpit.http is not a function') {
                    // wait a bit for the script to load and try again
                    await sleep(SCRIPT_LOAD_POLL_INTERVAL_MS);
                }
                else {
                    console.log(err);
                    throw err;
                }
            }
        }

        if (client === null) {
            throw new Error('Could not load Cockpit HTTP client.');
        }

        return client;
    }

    private async doLocalCockpitHttpGet(cockpitClient: any, apiUrl: string, headers?: any) {
        let response = null;
        let errorMessage: string | null = null;
        let responseOk = false;

        // set compulsory mvision api headers
        const httpHeaders = headers || {};
        httpHeaders['appVersion'] = get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = get(httpHeaders, 'user-agent', this.getAppVersion());

        // append client ID to query parameters
        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        this.debugLog('GET', apiUrl, { headers: httpHeaders, queryParams });

        try {
            response = await cockpitClient.get(apiUrl, queryParams, httpHeaders);
            responseOk = true;
            this.debugLog('GET', apiUrl, { response });
        }
        catch (ex) {
            responseOk = false;
            this.debugLog('GET', apiUrl, { error: ex });
            console.error(`Call to ${apiUrl} failed:`, ex);
            if (ex && get(ex, 'problem', '').includes('not-found')) {
                errorMessage = 'Could not connect to backend.';
            } else {
                errorMessage = this.getErrorMessageFromError(ex);
            }
        }

        return { responseOk, response, errorMessage };
    }

    private async getAndParseJsonResponse(apiUrl: string, appClient: MVisionAppClient | undefined, headers?: any, useRetries: boolean = false): Promise<any> {
        const client = await this.getHttpClient();

        // set compulsory mvision api headers
        const httpHeaders: any = headers ? headers : {};
        if (appClient) { httpHeaders['userId'] = get(httpHeaders, 'userId', appClient.userId); }
        httpHeaders['appVersion'] = get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();

        const doRequest = async () => {
            const apiCallResult = await this.doLocalCockpitHttpGet(client, apiUrl, httpHeaders);

            if (!apiCallResult.responseOk) {
                throw new Error(apiCallResult.errorMessage || 'Received no valid response from backend.');
            }

            if (!apiCallResult.response) {
                const message = `Received no valid response from backend.`;
                console.error(message);
                throw new Error(message);
            }

            return JSON.parse(apiCallResult.response);
        };

        if (!useRetries) {
            return await doRequest();
        }

        return await retryWithBackoff(
            doRequest,
            COCKPIT_RETRY_OPTIONS,
            (attempt, _err, delayMs) => {
                console.log(`Call to ${apiUrl} failed, retry ${attempt}/${COCKPIT_RETRY_OPTIONS.maxRetries} (waiting ${delayMs}ms)`);
            },
        );
    }

    private async sendJson(url: string, method: 'POST' | 'PUT', appClient: MVisionAppClient | undefined, headers?: any, json?: string): Promise<SaveResult> {
        const client = await this.getHttpClient();

        // set compulsory mvision api headers
        const httpHeaders: any = headers ? headers : {};
        httpHeaders['appVersion'] = get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();
        if (appClient) {
            httpHeaders['userId'] = get(httpHeaders, 'userId', appClient.userId);
        }

        // append client ID to query parameters
        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        const requestOptions: any = {
            path: url,
            method: method,
            params: queryParams,
            headers: httpHeaders,
        };

        if (json !== undefined) {
            requestOptions.body = json;
        }

        this.debugLog(method, url, { headers: httpHeaders, queryParams, body: json });

        const response = client.request(requestOptions);

        // cockpit API: must call http.request.input() if request doesn't have BODY
        if (json === undefined) {
            response.input();
        }

        // Cockpit's http.request returns a Promise that we must handle with
        // then/catch instead of async/await so we get the second catch parameter
        // when needed in the promise reject case
        const result = await response
            .then((data: any) => {
                this.debugLog(method, url, { response: data });
                return data;
            })
            .catch((ex: any, data: any) => {
                // return the received error data, we throw it
                // as an exception a bit later
                this.debugLog(method, url, { error: { exception: ex, data } });
                return data;
            });

        // Parse the cockpit response. This may throw for errors (concurrent save,
        // validation, etc.) — catch and re-throw with richer context when possible.
        try {
            return parseCockpitSendJsonResponse(result);
        } catch (e) {
            // ConcurrentSaveErrorWrapper must propagate as-is.
            if (e instanceof ConcurrentSaveErrorWrapper) {
                throw e;
            }
            // If we have the original request JSON, try to convert to a detailed validation error.
            if (json !== undefined) {
                let resultData: unknown;
                try {
                    resultData = JSON.parse(result ?? '');
                } catch {
                    // not valid JSON — just re-throw the original error
                }
                if (resultData !== undefined) {
                    throw await convertResponseToError(resultData, json, (e as Error).message);
                }
            }
            throw e;
        }
    }

    private async postJson(url: string, appClient: MVisionAppClient | undefined, headers?: any, json?: string): Promise<SaveResult> {
        return this.sendJson(url, 'POST', appClient, headers, json);
    }

    /** Sends a PUT request with optional JSON body. Unlike sendJson, this does not
     *  validate the response body format — a resolved promise means success, matching
     *  the behaviour of cloud-api's putToApi. */
    private async putJson(url: string, appClient: MVisionAppClient | undefined, headers?: any, json?: string): Promise<boolean> {
        const client = await this.getHttpClient();

        const httpHeaders: any = headers ? headers : {};
        httpHeaders['appVersion'] = get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();
        if (appClient) {
            httpHeaders['userId'] = get(httpHeaders, 'userId', appClient.userId);
        }

        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        const requestOptions: any = {
            path: url,
            method: 'PUT',
            params: queryParams,
            headers: httpHeaders,
        };

        if (json !== undefined) {
            requestOptions.body = json;
        }

        this.debugLog('PUT', url, { headers: httpHeaders, queryParams, body: json });

        const response = client.request(requestOptions);

        // cockpit HTTP client requires .input() when there is no body
        if (json === undefined) {
            response.input();
        }

        // Use .then/.catch instead of async/await to access the second .catch
        // parameter with backend error details (cockpit promise semantics).
        return await response
            .then(() => {
                this.debugLog('PUT', url, { response: 'OK' });
                return true;
            })
            .catch((ex: any, _data: any) => {
                this.debugLog('PUT', url, { error: ex });
                throw new Error(this.getErrorMessageFromError(ex));
            });
    }

    private async delete(url: string, appClient: MVisionAppClient, headers?: any): Promise<boolean> {
        const client = await this.getHttpClient();

        // set compulsory mvision api headers
        const httpHeaders: any = headers ? headers : {};
        httpHeaders['appVersion'] = get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();
        httpHeaders['userId'] = get(httpHeaders, 'userId', appClient.userId);

        // append client ID to query parameters
        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        this.debugLog('DELETE', url, { headers: httpHeaders, queryParams });

        const response = await client.request({
            path: url,
            method: 'DELETE',
            params: queryParams,
            headers: httpHeaders,
        }).input();

        this.debugLog('DELETE', url, { response });

        if (!response) {
            console.error(response);
            throw new Error(`Received no valid response from backend`);
        }

        return response === BACKEND_RESPONSE_OK_QUOTED;
    }

    public async fetchContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.contouringConfig, appClient, undefined, true);
    }

    // NOTE: force header must be a string 'true', not boolean true. The cockpit HTTP client
    // sends headers via a JSON protocol channel that preserves types — unlike fetch() which
    // auto-stringifies header values. A boolean true is not recognized by the backend.
    public async saveContouringCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(apiUrls.contouringConfig, appClient, force ? { force: 'true' } : undefined, json);
    }

    public async deleteContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(apiUrls.contouringConfig, appClient);
    }

    /** LOCAL: Returns default configuration for a single contouring model */
    public async fetchDefaultConfigurationForContouringModelAsync(modelName: string): Promise<any> {
        return await this.getAndParseJsonResponse(apiUrls.makeGetDefaultConfigForContourModelUrl(modelName), undefined, { userId: DEFAULT_USER_ID });
    }

    public async fetchDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.doseConfig, appClient, undefined, true);
    }

    public async saveDoseCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(apiUrls.doseConfig, appClient, force ? { force: 'true' } : undefined, json);
    }

    public async deleteDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(apiUrls.doseConfig, appClient);
    }

    /** LOCAL: Returns default configuration for a single dose model */
    public async fetchDefaultConfigurationForDoseModelAsync(modelName: string): Promise<any> {
        return await this.getAndParseJsonResponse(apiUrls.makeGetDefaultConfigForDoseModelUrl(modelName), undefined, { userId: DEFAULT_USER_ID });
    }

    public async fetchAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.adaptConfig, appClient, undefined, true);
    }

    public async saveAdaptCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(apiUrls.adaptConfig, appClient, force ? { force: 'true' } : undefined, json);
    }

    public async deleteAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(apiUrls.adaptConfig, appClient);
    }

    /** LOCAL: Returns default configuration for a single adapt model */
    public async fetchDefaultConfigurationForAdaptModelAsync(modelName: string): Promise<any> {
        return await this.getAndParseJsonResponse(apiUrls.makeGetDefaultConfigForAdaptModelUrl(modelName), undefined, { userId: DEFAULT_USER_ID });
    }

    public async fetchImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.imageConfig, appClient, undefined, true);
    }

    public async saveImageCustomizationAsync(appClient: MVisionAppClient | undefined, json: string, force?: boolean): Promise<SaveResult> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(apiUrls.imageConfig, appClient, force ? { force: 'true' } : undefined, json);
    }

    public async deleteImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(apiUrls.imageConfig, appClient);
    }

    /** LOCAL: Returns default configuration for a single image model */
    public async fetchDefaultConfigurationForImageModelAsync(modelName: string): Promise<any> {
        return await this.getAndParseJsonResponse(apiUrls.makeGetDefaultConfigForImageModelUrl(modelName), undefined, { userId: DEFAULT_USER_ID });
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.getLicenseUrl, appClient);
    }

    public async fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.daemonsConfig, appClient);
    }

    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.daemonConfig, appClient, { daemonSessionId: daemonSessionId });
    }

    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return (await this.postJson(apiUrls.daemonConfig, appClient, undefined, json)).success;
    }

    public async regenerateCredentialsAsync(appClient: MVisionAppClient | undefined, sessionID: string): Promise<any> {
        throw new Error('Token workflow is not implemented in Cockpit API');
    }

    public async tokenAssociationApproveAsync(appClient: MVisionAppClient | undefined, token: string, sessionID: string): Promise<string | null> {
        throw new Error('Token workflow is not implemented in Cockpit API');
    }

    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return (await this.postJson(apiUrls.resetDaemonConfig, appClient, { daemonSessionId: daemonSessionId })).success;
    }

    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(apiUrls.daemonConfig, appClient, { daemonSessionId: daemonSessionId });
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        const licenseJson = JSON.stringify({ license: newLicense });

        return (await this.postJson(apiUrls.saveLicenseUrl, appClient, undefined, licenseJson)).success;
    }

    public async fetchLabelingAsync(modelTypes: ModelType[]): Promise<CombinedVersionInfoJson> {
        const labelingJsons: LabelJsonWrapper[] = [];

        const backendJson = await this.getAndParseJsonResponse(apiUrls.backendVersionUrl, undefined);

        for (const modelType of modelTypes) {
            const labelingJson = await this.getAndParseJsonResponse(getLabelingUrl(modelType), undefined);
            labelingJsons.push({ modelType: modelType, labeling: labelingJson });
        }

        return { backendVersion: backendJson, labeling: labelingJsons };
    }


    public async fetchWorkspaceLabelingAsync(): Promise<WorkspaceLabeling> {
        return await this.getAndParseJsonResponse(apiUrls.workspaceLabeling, undefined);
    }

    public async fetchClientListAsync(_applicationsOnly?: boolean): Promise<any> {
        // Cockpit mode does not use the /#/cloud bootstrap flow, so the applicationsOnly
        // header is irrelevant here — the param exists only for interface compatibility.
        return await this.getAndParseJsonResponse(apiUrls.getClientListUrl, undefined, undefined, true);
    }

    public async fetchBackupsAsync(appClient: MVisionAppClient | undefined, modelType: string): Promise<BackupInfo[]> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.backupsConfig, appClient, { modelType });
    }

    public async downloadBackupAsync(appClient: MVisionAppClient | undefined, modelType: string, backupFilename: string): Promise<any> {
        this.checkAppClientIsValid(appClient);

        const client = await this.getHttpClient();
        const httpHeaders: any = {
            userId: appClient.userId,
            modelType,
            'backup_filename': backupFilename,
            appVersion: this.getAppVersion(),
            'user-agent': this.getAppVersion(),
        };

        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        this.debugLog('GET', apiUrls.backupDownload, { headers: httpHeaders, queryParams });

        // Use client.request().input() instead of client.get() — the .input() call
        // is required to signal end of input on bodyless requests in the cockpit HTTP client.
        let response: string;
        try {
            response = await client.request({
                path: apiUrls.backupDownload,
                method: 'GET',
                params: queryParams,
                headers: httpHeaders,
            }).input();
        } catch (ex) {
            this.debugLog('GET', apiUrls.backupDownload, { error: ex });
            throw new Error(this.getErrorMessageFromError(ex));
        }

        this.debugLog('GET', apiUrls.backupDownload, { response: '(backup data)' });

        if (!response) {
            throw new Error('Received no valid response from backend.');
        }

        return JSON.parse(response);
    }

    public async fetchActiveModelsAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(apiUrls.getListActiveModelsUrl, appClient, { modelType: convertModelTypeToApiString(modelType) });
    }    

    public async fetchAccessRightsAsync(): Promise<any> {
        return await this.getAndParseJsonResponse(apiUrls.getAccessRightsUrl, undefined);
    }

    public async exportCustomizationPdfAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<Blob> {
        this.checkAppClientIsValid(appClient);

        const client = await this.getHttpClient();
        const httpHeaders: any = {
            userId: appClient.userId,
            modelType: convertModelTypeToExportApiString(modelType),
            exportFormat: 'pdf',
            appVersion: this.getAppVersion(),
            'user-agent': this.getAppVersion(),
        };

        const queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        this.debugLog('GET', apiUrls.exportConfig, { headers: httpHeaders, queryParams });

        // Use cockpit HTTP request with binary option to avoid string encoding corruption.
        // Must call .input() on bodyless requests to signal end of input to the cockpit HTTP client.
        try {
            const response = await client.request({
                path: apiUrls.exportConfig,
                method: 'GET',
                params: queryParams,
                headers: httpHeaders,
                binary: true,
            }).input();
            this.debugLog('GET', apiUrls.exportConfig, { response: '(binary/PDF data)' });
            return new Blob([response], { type: 'application/pdf' });
        } catch (ex) {
            this.debugLog('GET', apiUrls.exportConfig, { error: ex });
            throw new Error(this.getErrorMessageFromError(ex));
        }
    }

    public async getUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<any> {
        const url = apiUrls.makeLocalUserSettingUrl(settingName);
        return await this.getAndParseJsonResponse(url, undefined);
    }

    public async saveUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string, jsonValue: string): Promise<boolean> {
        const url = apiUrls.makeLocalUserSettingUrl(settingName);
        return await this.putJson(url, undefined, undefined, jsonValue);
    }

    public async deleteUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        const url = apiUrls.makeLocalUserSettingUrl(settingName);
        return await this.delete(url, appClient);
    }

    // returns the "localEnvironmentTag" variable from config.json
    public getEnvironmentTag(): string {
        return environmentTag;
    }

    getAppVersion(): string {
        return getAppVersionHeader(_appVersion);
    }

    getAppWrapperComponent(): (props: React.PropsWithChildren<object>) => JSX.Element {
        return NoAuthPage;
    }

    checkAppClientIsValid(appClient: MVisionAppClient | undefined): asserts appClient is MVisionAppClient {
        if (appClient === undefined) {
            throw new Error('AppClient was undefined.');
        }
    }
}
